import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { 
  Save, ArrowLeft, Eye, Plus, X, Trash2, Upload, Image, Link2, 
  Type, Play, MessageSquare, Palette, Layout, GripVertical,
  Facebook, Instagram, Youtube, Twitter, Linkedin, Globe, Phone,
  Mail, MapPin, ExternalLink, Package, ShoppingBag
} from 'lucide-react';
import { FaWhatsapp, FaTelegram, FaTiktok, FaPinterest, FaSnapchat } from 'react-icons/fa';
import { toast } from 'sonner';
import api from '../../utils/api';
import { getImageUrl } from '../../utils/imageUrl';

const TEMPLATES = [
  { id: 'business', name: 'Business Card', icon: '💼', description: 'Compact professional card' },
  { id: 'landing', name: 'Landing Page', icon: '🚀', description: 'Full page with sections' },
  { id: 'portfolio', name: 'Portfolio', icon: '🎨', description: 'Image-focused gallery' },
  { id: 'luxury', name: 'Luxury', icon: '👑', description: 'Premium gold theme' },
  { id: 'minimal', name: 'Minimal', icon: '✨', description: 'Clean simple design' },
  { id: 'ecommerce', name: 'E-Commerce', icon: '🛒', description: 'Shop with products & gallery' }
];

const SOCIAL_PLATFORMS = [
  { id: 'whatsapp', name: 'WhatsApp', icon: FaWhatsapp, color: '#25D366' },
  { id: 'facebook', name: 'Facebook', icon: Facebook, color: '#1877F2' },
  { id: 'instagram', name: 'Instagram', icon: Instagram, color: '#E4405F' },
  { id: 'youtube', name: 'YouTube', icon: Youtube, color: '#FF0000' },
  { id: 'twitter', name: 'Twitter/X', icon: Twitter, color: '#1DA1F2' },
  { id: 'linkedin', name: 'LinkedIn', icon: Linkedin, color: '#0A66C2' },
  { id: 'telegram', name: 'Telegram', icon: FaTelegram, color: '#0088cc' },
  { id: 'tiktok', name: 'TikTok', icon: FaTiktok, color: '#000000' },
  { id: 'pinterest', name: 'Pinterest', icon: FaPinterest, color: '#E60023' },
  { id: 'website', name: 'Website', icon: Globe, color: '#6366F1' }
];

const FONTS = [
  'Inter', 'Roboto', 'Poppins', 'Montserrat', 'Playfair Display', 
  'Lora', 'Open Sans', 'Raleway', 'Nunito', 'Oswald'
];

const PageEditor = () => {
  const navigate = useNavigate();
  const { pageId } = useParams();
  const isEdit = !!pageId;
  const fileInputRef = useRef(null);
  const [activeUploadField, setActiveUploadField] = useState(null);

  const [loading, setLoading] = useState(isEdit);
  const [saving, setSaving] = useState(false);
  const [activeSection, setActiveSection] = useState('basic');
  const [previewMode, setPreviewMode] = useState(false);

  const [formData, setFormData] = useState({
    title: '',
    slug: '',
    template: 'luxury',
    headline: '',
    tagline: '',
    description: '',
    logo_url: '',
    profile_image: '',
    cover_image: '',
    email: '',
    phone: '',
    whatsapp_number: '',
    whatsapp_message: 'Hello! I found you through your page.',
    address: '',
    social_links: [],
    gallery_images: [],
    gallery_title: 'Gallery',
    text_sections: [],
    cta_buttons: [],
    video_url: '',
    video_title: '',
    enable_contact_form: false,
    contact_form_title: 'Contact Us',
    contact_form_fields: [
      { name: 'name', field_type: 'text', required: true },
      { name: 'email', field_type: 'email', required: true },
      { name: 'message', field_type: 'textarea', required: true }
    ],
    // E-commerce fields
    products: [],
    products_title: 'Our Products',
    enable_products: false,
    shop_whatsapp: '',
    primary_color: '#D4AF37',
    secondary_color: '#1a1a2e',
    background_color: '#0a0a0a',
    text_color: '#ffffff',
    font_family: 'Inter',
    meta_title: '',
    meta_description: '',
    is_published: false,
    show_branding: true
  });

  const [newSocialLink, setNewSocialLink] = useState({ platform: 'whatsapp', url: '' });
  const [newTextSection, setNewTextSection] = useState({ title: '', content: '' });
  const [newCTAButton, setNewCTAButton] = useState({ text: '', url: '', style: 'primary' });
  const [newProduct, setNewProduct] = useState({ name: '', price: '', image: '', description: '' });
  const [productUploadIndex, setProductUploadIndex] = useState(null);

  useEffect(() => {
    if (isEdit) {
      fetchPage();
    }
  }, [pageId]);

  const fetchPage = async () => {
    try {
      const res = await api.get('/admin/pages/all');
      const page = res.data.pages.find(p => p.id === pageId);
      if (page) {
        setFormData({
          ...formData,
          ...page,
          contact_form_fields: page.contact_form_fields?.length > 0 ? page.contact_form_fields : formData.contact_form_fields
        });
      } else {
        toast.error('Page not found');
        navigate('/admin/page-builder');
      }
    } catch (error) {
      toast.error('Failed to load page');
    } finally {
      setLoading(false);
    }
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !activeUploadField) return;

    const allowedTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      toast.error('Invalid image type');
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      toast.error('Image must be less than 5MB');
      return;
    }

    try {
      const uploadData = new FormData();
      uploadData.append('file', file);
      const res = await api.post('/upload/image', uploadData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      if (res.data?.url) {
        if (activeUploadField === 'gallery') {
          setFormData(prev => ({
            ...prev,
            gallery_images: [...prev.gallery_images, { url: res.data.url, caption: '', order: prev.gallery_images.length }]
          }));
        } else if (activeUploadField === 'product') {
          // Handle product image upload
          if (productUploadIndex === -1) {
            // New product
            setNewProduct(prev => ({ ...prev, image: res.data.url }));
          } else if (productUploadIndex !== null) {
            // Existing product
            setFormData(prev => ({
              ...prev,
              products: prev.products.map((p, i) => 
                i === productUploadIndex ? { ...p, image: res.data.url } : p
              )
            }));
          }
          setProductUploadIndex(null);
        } else {
          setFormData(prev => ({ ...prev, [activeUploadField]: res.data.url }));
        }
        toast.success('Image uploaded!');
      }
    } catch (error) {
      toast.error('Upload failed');
    }
    setActiveUploadField(null);
  };

  const triggerUpload = (field) => {
    setActiveUploadField(field);
    fileInputRef.current?.click();
  };

  const generateSlug = (title) => {
    return title.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');
  };

  const addSocialLink = () => {
    if (!newSocialLink.url) return;
    let url = newSocialLink.url.trim();
    if (!url.match(/^https?:\/\//i)) url = 'https://' + url;
    
    setFormData(prev => ({
      ...prev,
      social_links: [...prev.social_links, { ...newSocialLink, url }]
    }));
    setNewSocialLink({ platform: 'whatsapp', url: '' });
  };

  const removeSocialLink = (index) => {
    setFormData(prev => ({
      ...prev,
      social_links: prev.social_links.filter((_, i) => i !== index)
    }));
  };

  const addTextSection = () => {
    if (!newTextSection.title || !newTextSection.content) return;
    setFormData(prev => ({
      ...prev,
      text_sections: [...prev.text_sections, { ...newTextSection, order: prev.text_sections.length }]
    }));
    setNewTextSection({ title: '', content: '' });
  };

  const removeTextSection = (index) => {
    setFormData(prev => ({
      ...prev,
      text_sections: prev.text_sections.filter((_, i) => i !== index)
    }));
  };

  const addCTAButton = () => {
    if (!newCTAButton.text || !newCTAButton.url) return;
    let url = newCTAButton.url.trim();
    if (!url.match(/^https?:\/\//i) && !url.startsWith('tel:') && !url.startsWith('mailto:')) {
      url = 'https://' + url;
    }
    setFormData(prev => ({
      ...prev,
      cta_buttons: [...prev.cta_buttons, { ...newCTAButton, url, order: prev.cta_buttons.length }]
    }));
    setNewCTAButton({ text: '', url: '', style: 'primary' });
  };

  const removeCTAButton = (index) => {
    setFormData(prev => ({
      ...prev,
      cta_buttons: prev.cta_buttons.filter((_, i) => i !== index)
    }));
  };

  const removeGalleryImage = (index) => {
    setFormData(prev => ({
      ...prev,
      gallery_images: prev.gallery_images.filter((_, i) => i !== index)
    }));
  };

  const handleSave = async () => {
    if (!formData.title || !formData.slug) {
      toast.error('Title and slug are required');
      return;
    }

    setSaving(true);
    try {
      if (isEdit) {
        await api.put(`/admin/pages/update/${pageId}`, formData);
        toast.success('Page updated!');
      } else {
        await api.post('/admin/pages/create', formData);
        toast.success('Page created!');
        navigate('/admin/page-builder');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const handlePublish = async () => {
    if (!formData.title || !formData.slug) {
      toast.error('Save the page first');
      return;
    }
    
    setSaving(true);
    try {
      const newPublishState = !formData.is_published;
      if (isEdit) {
        await api.put(`/admin/pages/update/${pageId}`, { ...formData, is_published: newPublishState });
      } else {
        await api.post('/admin/pages/create', { ...formData, is_published: newPublishState });
      }
      setFormData(prev => ({ ...prev, is_published: newPublishState }));
      toast.success(newPublishState ? 'Page Published!' : 'Page Unpublished');
    } catch (error) {
      toast.error('Failed to update publish status');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin w-8 h-8 border-4 border-primary border-t-transparent rounded-full"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen" data-testid="page-editor">
      {/* Hidden file input */}
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        onChange={handleImageUpload}
        className="hidden"
      />

      {/* Header */}
      <div className="sticky top-0 z-10 bg-background/95 backdrop-blur border-b border-white/10 p-4">
        <div className="flex items-center justify-between max-w-7xl mx-auto">
          <div className="flex items-center gap-4">
            <button onClick={() => navigate('/admin/page-builder')} className="p-2 hover:bg-white/10 rounded-lg">
              <ArrowLeft className="w-5 h-5" />
            </button>
            <div>
              <h1 className="text-xl font-bold">{isEdit ? 'Edit Page' : 'Create New Page'}</h1>
              <p className="text-sm text-muted-foreground">{formData.title || 'Untitled'}</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {formData.slug && (
              <a
                href={`/p/${formData.slug}`}
                target="_blank"
                rel="noreferrer"
                className="px-3 py-2 bg-white/5 hover:bg-white/10 rounded-lg flex items-center gap-2"
              >
                <Eye className="w-4 h-4" />
                Preview
              </a>
            )}
            <button
              onClick={handlePublish}
              disabled={saving}
              className={`px-4 py-2 font-semibold rounded-lg flex items-center gap-2 disabled:opacity-50 ${
                formData.is_published 
                  ? 'bg-orange-500 text-white' 
                  : 'bg-green-500 text-white'
              }`}
            >
              {formData.is_published ? 'Unpublish' : 'Publish'}
            </button>
            <button
              onClick={handleSave}
              disabled={saving}
              className="px-4 py-2 bg-primary text-black font-semibold rounded-lg flex items-center gap-2 disabled:opacity-50"
            >
              <Save className="w-4 h-4" />
              {saving ? 'Saving...' : 'Save'}
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto p-4 grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left Panel - Editor */}
        <div className="lg:col-span-2 space-y-6">
          {/* Section Tabs */}
          <div className="flex gap-2 overflow-x-auto pb-2">
            {[
              { id: 'basic', label: 'Basic Info', icon: Type },
              { id: 'images', label: 'Images', icon: Image },
              { id: 'contact', label: 'Contact', icon: Phone },
              { id: 'social', label: 'Social Links', icon: Link2 },
              { id: 'gallery', label: 'Gallery', icon: Image },
              { id: 'products', label: 'Products', icon: Package },
              { id: 'content', label: 'Content', icon: Type },
              { id: 'cta', label: 'CTA Buttons', icon: ExternalLink },
              { id: 'video', label: 'Video', icon: Play },
              { id: 'form', label: 'Contact Form', icon: MessageSquare },
              { id: 'style', label: 'Styling', icon: Palette },
              { id: 'seo', label: 'SEO', icon: Globe }
            ].map(section => (
              <button
                key={section.id}
                onClick={() => setActiveSection(section.id)}
                className={`px-4 py-2 rounded-lg flex items-center gap-2 whitespace-nowrap transition-all ${
                  activeSection === section.id 
                    ? 'bg-primary text-black' 
                    : 'bg-white/5 hover:bg-white/10'
                }`}
              >
                <section.icon className="w-4 h-4" />
                {section.label}
              </button>
            ))}
          </div>

          {/* Basic Info */}
          {activeSection === 'basic' && (
            <div className="glass-card p-6 space-y-4">
              <h2 className="text-lg font-bold flex items-center gap-2">
                <Type className="w-5 h-5 text-primary" />
                Basic Information
              </h2>

              <div>
                <label className="block text-sm mb-1">Page Title *</label>
                <input
                  type="text"
                  value={formData.title}
                  onChange={(e) => {
                    setFormData({ 
                      ...formData, 
                      title: e.target.value,
                      slug: formData.slug || generateSlug(e.target.value)
                    });
                  }}
                  className="w-full px-4 py-2 bg-white/5 border border-white/10 rounded-xl"
                  placeholder="My Business Page"
                />
              </div>

              <div>
                <label className="block text-sm mb-1">URL Slug *</label>
                <div className="flex items-center gap-2">
                  <span className="text-muted-foreground">/p/</span>
                  <input
                    type="text"
                    value={formData.slug}
                    onChange={(e) => setFormData({ ...formData, slug: generateSlug(e.target.value) })}
                    className="flex-1 px-4 py-2 bg-white/5 border border-white/10 rounded-xl"
                    placeholder="my-business"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm mb-1">Template</label>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                  {TEMPLATES.map(template => (
                    <button
                      key={template.id}
                      onClick={() => setFormData({ ...formData, template: template.id })}
                      className={`p-3 rounded-xl border-2 text-left transition-all ${
                        formData.template === template.id 
                          ? 'border-primary bg-primary/10' 
                          : 'border-white/10 hover:border-white/30'
                      }`}
                    >
                      <span className="text-2xl">{template.icon}</span>
                      <p className="font-medium mt-1">{template.name}</p>
                      <p className="text-xs text-muted-foreground">{template.description}</p>
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <label className="block text-sm mb-1">Headline</label>
                <input
                  type="text"
                  value={formData.headline}
                  onChange={(e) => setFormData({ ...formData, headline: e.target.value })}
                  className="w-full px-4 py-2 bg-white/5 border border-white/10 rounded-xl"
                  placeholder="Welcome to My Page"
                />
              </div>

              <div>
                <label className="block text-sm mb-1">Tagline</label>
                <input
                  type="text"
                  value={formData.tagline}
                  onChange={(e) => setFormData({ ...formData, tagline: e.target.value })}
                  className="w-full px-4 py-2 bg-white/5 border border-white/10 rounded-xl"
                  placeholder="Your tagline here"
                />
              </div>

              <div>
                <label className="block text-sm mb-1">Description</label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  className="w-full px-4 py-2 bg-white/5 border border-white/10 rounded-xl h-24"
                  placeholder="Brief description..."
                />
              </div>

              <div className="flex items-center gap-4">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={formData.is_published}
                    onChange={(e) => setFormData({ ...formData, is_published: e.target.checked })}
                    className="w-4 h-4"
                  />
                  <span>Publish Page</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={formData.show_branding}
                    onChange={(e) => setFormData({ ...formData, show_branding: e.target.checked })}
                    className="w-4 h-4"
                  />
                  <span>Show Branding</span>
                </label>
              </div>
            </div>
          )}

          {/* Images */}
          {activeSection === 'images' && (
            <div className="glass-card p-6 space-y-4">
              <h2 className="text-lg font-bold flex items-center gap-2">
                <Image className="w-5 h-5 text-primary" />
                Images
              </h2>

              {['logo_url', 'profile_image', 'cover_image'].map(field => (
                <div key={field} className="p-4 bg-white/5 rounded-xl">
                  <label className="block text-sm mb-2 capitalize">{field.replace('_', ' ').replace('url', '')}</label>
                  <div className="flex gap-4 items-start">
                    <div 
                      onClick={() => triggerUpload(field)}
                      className={`${field === 'cover_image' ? 'w-40 h-24' : 'w-20 h-20'} rounded-lg border-2 border-dashed border-white/20 flex items-center justify-center cursor-pointer hover:border-primary/50 transition-all overflow-hidden bg-white/5`}
                    >
                      {formData[field] ? (
                        <img src={getImageUrl(formData[field])} alt="" className="w-full h-full object-cover" />
                      ) : (
                        <Upload className="w-6 h-6 text-gray-500" />
                      )}
                    </div>
                    <div className="flex-1">
                      <button
                        onClick={() => triggerUpload(field)}
                        className="px-3 py-2 bg-primary/20 text-primary rounded-lg text-sm flex items-center gap-2 mb-2"
                      >
                        <Upload className="w-4 h-4" />
                        Upload
                      </button>
                      <input
                        type="url"
                        value={formData[field]}
                        onChange={(e) => setFormData({ ...formData, [field]: e.target.value })}
                        className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-sm"
                        placeholder="Or paste URL..."
                      />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Contact */}
          {activeSection === 'contact' && (
            <div className="glass-card p-6 space-y-4">
              <h2 className="text-lg font-bold flex items-center gap-2">
                <Phone className="w-5 h-5 text-primary" />
                Contact Information
              </h2>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm mb-1 flex items-center gap-2">
                    <Mail className="w-4 h-4" /> Email
                  </label>
                  <input
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    className="w-full px-4 py-2 bg-white/5 border border-white/10 rounded-xl"
                    placeholder="email@example.com"
                  />
                </div>
                <div>
                  <label className="block text-sm mb-1 flex items-center gap-2">
                    <Phone className="w-4 h-4" /> Phone
                  </label>
                  <input
                    type="tel"
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                    className="w-full px-4 py-2 bg-white/5 border border-white/10 rounded-xl"
                    placeholder="+91 9876543210"
                  />
                </div>
              </div>

              <div className="p-4 bg-green-500/10 border border-green-500/20 rounded-xl">
                <label className="block text-sm mb-1 flex items-center gap-2 text-green-400">
                  <FaWhatsapp className="w-4 h-4" /> WhatsApp Number
                </label>
                <input
                  type="tel"
                  value={formData.whatsapp_number}
                  onChange={(e) => setFormData({ ...formData, whatsapp_number: e.target.value })}
                  className="w-full px-4 py-2 bg-white/5 border border-white/10 rounded-xl mb-2"
                  placeholder="919876543210 (with country code, no +)"
                />
                <label className="block text-sm mb-1">Pre-filled Message</label>
                <input
                  type="text"
                  value={formData.whatsapp_message}
                  onChange={(e) => setFormData({ ...formData, whatsapp_message: e.target.value })}
                  className="w-full px-4 py-2 bg-white/5 border border-white/10 rounded-xl"
                  placeholder="Hello! I found you through your page."
                />
              </div>

              <div>
                <label className="block text-sm mb-1 flex items-center gap-2">
                  <MapPin className="w-4 h-4" /> Address
                </label>
                <textarea
                  value={formData.address}
                  onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                  className="w-full px-4 py-2 bg-white/5 border border-white/10 rounded-xl h-20"
                  placeholder="Your business address..."
                />
              </div>
            </div>
          )}

          {/* Social Links */}
          {activeSection === 'social' && (
            <div className="glass-card p-6 space-y-4">
              <h2 className="text-lg font-bold flex items-center gap-2">
                <Link2 className="w-5 h-5 text-primary" />
                Social Links
              </h2>

              {/* Existing Links */}
              <div className="space-y-2">
                {formData.social_links.map((link, index) => {
                  const platform = SOCIAL_PLATFORMS.find(p => p.id === link.platform);
                  const Icon = platform?.icon || Globe;
                  return (
                    <div key={index} className="flex items-center gap-3 p-3 bg-white/5 rounded-xl">
                      <Icon className="w-5 h-5" style={{ color: platform?.color }} />
                      <span className="font-medium">{platform?.name || link.platform}</span>
                      <span className="flex-1 text-sm text-muted-foreground truncate">{link.url}</span>
                      <button onClick={() => removeSocialLink(index)} className="p-1 hover:bg-red-500/20 rounded">
                        <X className="w-4 h-4 text-red-400" />
                      </button>
                    </div>
                  );
                })}
              </div>

              {/* Add New */}
              <div className="p-4 bg-white/5 rounded-xl space-y-3">
                <p className="text-sm font-medium">Add Social Link</p>
                <div className="grid grid-cols-5 gap-2">
                  {SOCIAL_PLATFORMS.map(platform => {
                    const Icon = platform.icon;
                    return (
                      <button
                        key={platform.id}
                        onClick={() => setNewSocialLink({ ...newSocialLink, platform: platform.id })}
                        className={`p-2 rounded-lg flex flex-col items-center gap-1 transition-all ${
                          newSocialLink.platform === platform.id 
                            ? 'bg-white/20 ring-2 ring-primary' 
                            : 'bg-white/5 hover:bg-white/10'
                        }`}
                      >
                        <Icon className="w-5 h-5" style={{ color: platform.color }} />
                        <span className="text-[10px]">{platform.name}</span>
                      </button>
                    );
                  })}
                </div>
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={newSocialLink.url}
                    onChange={(e) => setNewSocialLink({ ...newSocialLink, url: e.target.value })}
                    className="flex-1 px-4 py-2 bg-white/5 border border-white/10 rounded-xl"
                    placeholder="Enter URL or username..."
                  />
                  <button
                    onClick={addSocialLink}
                    className="px-4 py-2 bg-primary text-black rounded-xl"
                  >
                    <Plus className="w-5 h-5" />
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Gallery */}
          {activeSection === 'gallery' && (
            <div className="glass-card p-6 space-y-4">
              <h2 className="text-lg font-bold flex items-center gap-2">
                <Image className="w-5 h-5 text-primary" />
                Photo Gallery
              </h2>

              <div>
                <label className="block text-sm mb-1">Gallery Title</label>
                <input
                  type="text"
                  value={formData.gallery_title}
                  onChange={(e) => setFormData({ ...formData, gallery_title: e.target.value })}
                  className="w-full px-4 py-2 bg-white/5 border border-white/10 rounded-xl"
                  placeholder="Gallery"
                />
              </div>

              <div className="grid grid-cols-3 md:grid-cols-4 gap-3">
                {formData.gallery_images.map((img, index) => (
                  <div key={index} className="relative group">
                    <img src={getImageUrl(img.url)} alt="" className="w-full h-24 object-cover rounded-lg" />
                    <button
                      onClick={() => removeGalleryImage(index)}
                      className="absolute top-1 right-1 p-1 bg-red-500 rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
                    >
                      <X className="w-3 h-3" />
                    </button>
                  </div>
                ))}
                <button
                  onClick={() => triggerUpload('gallery')}
                  className="h-24 border-2 border-dashed border-white/20 rounded-lg flex flex-col items-center justify-center hover:border-primary/50 transition-all"
                >
                  <Plus className="w-6 h-6 text-gray-500" />
                  <span className="text-xs text-gray-500">Add Image</span>
                </button>
              </div>
            </div>
          )}

          {/* Products Section (E-commerce) */}
          {activeSection === 'products' && (
            <div className="glass-card p-6 space-y-4">
              <h2 className="text-lg font-bold flex items-center gap-2">
                <ShoppingBag className="w-5 h-5 text-primary" />
                Products / Shop
              </h2>
              
              <div className="flex items-center justify-between p-3 bg-white/5 rounded-lg">
                <div>
                  <span className="font-medium">Enable Products Section</span>
                  <p className="text-xs text-muted-foreground">Show products on your page</p>
                </div>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={formData.enable_products || false}
                    onChange={(e) => setFormData({ ...formData, enable_products: e.target.checked })}
                    className="sr-only peer"
                  />
                  <div className="w-11 h-6 bg-white/20 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary"></div>
                </label>
              </div>

              <div>
                <label className="block text-sm mb-2">Products Section Title</label>
                <input
                  type="text"
                  value={formData.products_title || 'Our Products'}
                  onChange={(e) => setFormData({ ...formData, products_title: e.target.value })}
                  className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl"
                  placeholder="Our Products"
                />
              </div>

              <div>
                <label className="block text-sm mb-2">Shop WhatsApp (for inquiries)</label>
                <input
                  type="text"
                  value={formData.shop_whatsapp || ''}
                  onChange={(e) => setFormData({ ...formData, shop_whatsapp: e.target.value })}
                  className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl"
                  placeholder="919876543210"
                />
              </div>

              {/* Product List */}
              <div className="space-y-3">
                <p className="text-sm font-medium">Products ({(formData.products || []).length})</p>
                {(formData.products || []).map((product, index) => (
                  <div key={index} className="flex gap-3 p-3 bg-white/5 rounded-xl">
                    {product.image && (
                      <img src={getImageUrl(product.image)} alt="" className="w-16 h-16 object-cover rounded-lg" />
                    )}
                    <div className="flex-1 min-w-0">
                      <h4 className="font-medium truncate">{product.name}</h4>
                      <p className="text-sm text-primary">₹{product.price}</p>
                      <p className="text-xs text-muted-foreground line-clamp-1">{product.description}</p>
                    </div>
                    <button
                      onClick={() => setFormData(prev => ({
                        ...prev,
                        products: prev.products.filter((_, i) => i !== index)
                      }))}
                      className="p-2 hover:bg-red-500/20 rounded-lg self-start"
                    >
                      <Trash2 className="w-4 h-4 text-red-400" />
                    </button>
                  </div>
                ))}
              </div>

              {/* Add New Product */}
              <div className="p-4 bg-white/5 rounded-xl space-y-3">
                <p className="text-sm font-medium">Add New Product</p>
                <div className="grid grid-cols-2 gap-3">
                  <input
                    type="text"
                    value={newProduct.name}
                    onChange={(e) => setNewProduct({ ...newProduct, name: e.target.value })}
                    className="px-3 py-2 bg-white/5 border border-white/10 rounded-lg"
                    placeholder="Product Name"
                  />
                  <input
                    type="number"
                    value={newProduct.price}
                    onChange={(e) => setNewProduct({ ...newProduct, price: e.target.value })}
                    className="px-3 py-2 bg-white/5 border border-white/10 rounded-lg"
                    placeholder="Price (₹)"
                  />
                </div>
                <textarea
                  value={newProduct.description}
                  onChange={(e) => setNewProduct({ ...newProduct, description: e.target.value })}
                  className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-lg h-20"
                  placeholder="Product description..."
                />
                <div className="flex gap-3">
                  <input
                    type="text"
                    value={newProduct.image}
                    onChange={(e) => setNewProduct({ ...newProduct, image: e.target.value })}
                    className="flex-1 px-3 py-2 bg-white/5 border border-white/10 rounded-lg"
                    placeholder="Image URL or upload"
                  />
                  <button
                    onClick={() => {
                      setProductUploadIndex(-1); // -1 means new product
                      triggerUpload('product');
                    }}
                    className="px-3 py-2 bg-white/10 rounded-lg hover:bg-white/20"
                  >
                    <Upload className="w-4 h-4" />
                  </button>
                </div>
                <button
                  onClick={() => {
                    if (!newProduct.name || !newProduct.price) {
                      toast.error('Name and price required');
                      return;
                    }
                    setFormData(prev => ({
                      ...prev,
                      products: [...(prev.products || []), { ...newProduct, id: Date.now() }]
                    }));
                    setNewProduct({ name: '', price: '', image: '', description: '' });
                    toast.success('Product added');
                  }}
                  className="w-full px-4 py-2 bg-primary text-black rounded-xl flex items-center justify-center gap-2"
                >
                  <Plus className="w-4 h-4" />
                  Add Product
                </button>
              </div>
            </div>
          )}

          {/* Content Sections */}
          {activeSection === 'content' && (
            <div className="glass-card p-6 space-y-4">
              <h2 className="text-lg font-bold flex items-center gap-2">
                <Type className="w-5 h-5 text-primary" />
                Text Sections
              </h2>

              {/* Existing Sections */}
              <div className="space-y-3">
                {formData.text_sections.map((section, index) => (
                  <div key={index} className="p-4 bg-white/5 rounded-xl">
                    <div className="flex items-center justify-between mb-2">
                      <h4 className="font-medium">{section.title}</h4>
                      <button onClick={() => removeTextSection(index)} className="p-1 hover:bg-red-500/20 rounded">
                        <Trash2 className="w-4 h-4 text-red-400" />
                      </button>
                    </div>
                    <p className="text-sm text-muted-foreground line-clamp-2">{section.content}</p>
                  </div>
                ))}
              </div>

              {/* Add New */}
              <div className="p-4 bg-white/5 rounded-xl space-y-3">
                <p className="text-sm font-medium">Add Section</p>
                <input
                  type="text"
                  value={newTextSection.title}
                  onChange={(e) => setNewTextSection({ ...newTextSection, title: e.target.value })}
                  className="w-full px-4 py-2 bg-white/5 border border-white/10 rounded-xl"
                  placeholder="Section Title"
                />
                <textarea
                  value={newTextSection.content}
                  onChange={(e) => setNewTextSection({ ...newTextSection, content: e.target.value })}
                  className="w-full px-4 py-2 bg-white/5 border border-white/10 rounded-xl h-24"
                  placeholder="Section content..."
                />
                <button
                  onClick={addTextSection}
                  className="px-4 py-2 bg-primary text-black rounded-xl flex items-center gap-2"
                >
                  <Plus className="w-4 h-4" />
                  Add Section
                </button>
              </div>
            </div>
          )}

          {/* CTA Buttons */}
          {activeSection === 'cta' && (
            <div className="glass-card p-6 space-y-4">
              <h2 className="text-lg font-bold flex items-center gap-2">
                <ExternalLink className="w-5 h-5 text-primary" />
                Call-to-Action Buttons
              </h2>

              {/* Existing Buttons */}
              <div className="space-y-2">
                {formData.cta_buttons.map((btn, index) => (
                  <div key={index} className="flex items-center gap-3 p-3 bg-white/5 rounded-xl">
                    <span className={`px-3 py-1 rounded-lg text-sm ${
                      btn.style === 'primary' ? 'bg-primary text-black' :
                      btn.style === 'secondary' ? 'bg-white/20' : 'border border-white/30'
                    }`}>
                      {btn.text}
                    </span>
                    <span className="flex-1 text-sm text-muted-foreground truncate">{btn.url}</span>
                    <button onClick={() => removeCTAButton(index)} className="p-1 hover:bg-red-500/20 rounded">
                      <X className="w-4 h-4 text-red-400" />
                    </button>
                  </div>
                ))}
              </div>

              {/* Add New */}
              <div className="p-4 bg-white/5 rounded-xl space-y-3">
                <p className="text-sm font-medium">Add Button</p>
                <div className="grid grid-cols-2 gap-3">
                  <input
                    type="text"
                    value={newCTAButton.text}
                    onChange={(e) => setNewCTAButton({ ...newCTAButton, text: e.target.value })}
                    className="px-4 py-2 bg-white/5 border border-white/10 rounded-xl"
                    placeholder="Button Text"
                  />
                  <input
                    type="text"
                    value={newCTAButton.url}
                    onChange={(e) => setNewCTAButton({ ...newCTAButton, url: e.target.value })}
                    className="px-4 py-2 bg-white/5 border border-white/10 rounded-xl"
                    placeholder="URL"
                  />
                </div>
                <div className="flex gap-2">
                  {['primary', 'secondary', 'outline'].map(style => (
                    <button
                      key={style}
                      onClick={() => setNewCTAButton({ ...newCTAButton, style })}
                      className={`px-3 py-2 rounded-lg capitalize ${
                        newCTAButton.style === style ? 'ring-2 ring-primary' : ''
                      } ${
                        style === 'primary' ? 'bg-primary text-black' :
                        style === 'secondary' ? 'bg-white/20' : 'border border-white/30'
                      }`}
                    >
                      {style}
                    </button>
                  ))}
                </div>
                <button
                  onClick={addCTAButton}
                  className="px-4 py-2 bg-primary text-black rounded-xl flex items-center gap-2"
                >
                  <Plus className="w-4 h-4" />
                  Add Button
                </button>
              </div>
            </div>
          )}

          {/* Video */}
          {activeSection === 'video' && (
            <div className="glass-card p-6 space-y-4">
              <h2 className="text-lg font-bold flex items-center gap-2">
                <Play className="w-5 h-5 text-primary" />
                Video Embed
              </h2>

              <div>
                <label className="block text-sm mb-1">Video Title</label>
                <input
                  type="text"
                  value={formData.video_title}
                  onChange={(e) => setFormData({ ...formData, video_title: e.target.value })}
                  className="w-full px-4 py-2 bg-white/5 border border-white/10 rounded-xl"
                  placeholder="Featured Video"
                />
              </div>

              <div>
                <label className="block text-sm mb-1">YouTube/Vimeo URL</label>
                <input
                  type="url"
                  value={formData.video_url}
                  onChange={(e) => setFormData({ ...formData, video_url: e.target.value })}
                  className="w-full px-4 py-2 bg-white/5 border border-white/10 rounded-xl"
                  placeholder="https://youtube.com/watch?v=..."
                />
                <p className="text-xs text-muted-foreground mt-1">Paste YouTube or Vimeo video URL</p>
              </div>

              {formData.video_url && (
                <div className="aspect-video bg-black/50 rounded-xl overflow-hidden">
                  <iframe
                    src={formData.video_url.replace('watch?v=', 'embed/').replace('youtu.be/', 'youtube.com/embed/')}
                    className="w-full h-full"
                    allowFullScreen
                  />
                </div>
              )}
            </div>
          )}

          {/* Contact Form */}
          {activeSection === 'form' && (
            <div className="glass-card p-6 space-y-4">
              <h2 className="text-lg font-bold flex items-center gap-2">
                <MessageSquare className="w-5 h-5 text-primary" />
                Contact Form
              </h2>

              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={formData.enable_contact_form}
                  onChange={(e) => setFormData({ ...formData, enable_contact_form: e.target.checked })}
                  className="w-4 h-4"
                />
                <span>Enable Contact Form</span>
              </label>

              {formData.enable_contact_form && (
                <>
                  <div>
                    <label className="block text-sm mb-1">Form Title</label>
                    <input
                      type="text"
                      value={formData.contact_form_title}
                      onChange={(e) => setFormData({ ...formData, contact_form_title: e.target.value })}
                      className="w-full px-4 py-2 bg-white/5 border border-white/10 rounded-xl"
                      placeholder="Contact Us"
                    />
                  </div>

                  <div className="p-4 bg-white/5 rounded-xl">
                    <p className="text-sm font-medium mb-3">Form Fields Preview</p>
                    <div className="space-y-3">
                      {formData.contact_form_fields.map((field, index) => (
                        <div key={index}>
                          <label className="block text-sm mb-1 capitalize">
                            {field.name} {field.required && <span className="text-red-400">*</span>}
                          </label>
                          {field.field_type === 'textarea' ? (
                            <textarea className="w-full px-4 py-2 bg-white/10 border border-white/10 rounded-xl" disabled />
                          ) : (
                            <input type={field.field_type} className="w-full px-4 py-2 bg-white/10 border border-white/10 rounded-xl" disabled />
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                </>
              )}
            </div>
          )}

          {/* Styling */}
          {activeSection === 'style' && (
            <div className="glass-card p-6 space-y-4">
              <h2 className="text-lg font-bold flex items-center gap-2">
                <Palette className="w-5 h-5 text-primary" />
                Styling
              </h2>

              <div className="grid grid-cols-2 gap-4">
                {[
                  { key: 'primary_color', label: 'Primary Color' },
                  { key: 'secondary_color', label: 'Secondary Color' },
                  { key: 'background_color', label: 'Background' },
                  { key: 'text_color', label: 'Text Color' }
                ].map(color => (
                  <div key={color.key}>
                    <label className="block text-sm mb-1">{color.label}</label>
                    <div className="flex gap-2">
                      <input
                        type="color"
                        value={formData[color.key]}
                        onChange={(e) => setFormData({ ...formData, [color.key]: e.target.value })}
                        className="w-12 h-10 rounded cursor-pointer"
                      />
                      <input
                        type="text"
                        value={formData[color.key]}
                        onChange={(e) => setFormData({ ...formData, [color.key]: e.target.value })}
                        className="flex-1 px-4 py-2 bg-white/5 border border-white/10 rounded-xl font-mono text-sm"
                      />
                    </div>
                  </div>
                ))}
              </div>

              <div>
                <label className="block text-sm mb-1">Font Family</label>
                <select
                  value={formData.font_family}
                  onChange={(e) => setFormData({ ...formData, font_family: e.target.value })}
                  className="w-full px-4 py-2 bg-white/5 border border-white/10 rounded-xl"
                >
                  {FONTS.map(font => (
                    <option key={font} value={font}>{font}</option>
                  ))}
                </select>
              </div>
            </div>
          )}

          {/* SEO */}
          {activeSection === 'seo' && (
            <div className="glass-card p-6 space-y-4">
              <h2 className="text-lg font-bold flex items-center gap-2">
                <Globe className="w-5 h-5 text-primary" />
                SEO Settings
              </h2>

              <div>
                <label className="block text-sm mb-1">Meta Title</label>
                <input
                  type="text"
                  value={formData.meta_title}
                  onChange={(e) => setFormData({ ...formData, meta_title: e.target.value })}
                  className="w-full px-4 py-2 bg-white/5 border border-white/10 rounded-xl"
                  placeholder="Page title for search engines"
                />
              </div>

              <div>
                <label className="block text-sm mb-1">Meta Description</label>
                <textarea
                  value={formData.meta_description}
                  onChange={(e) => setFormData({ ...formData, meta_description: e.target.value })}
                  className="w-full px-4 py-2 bg-white/5 border border-white/10 rounded-xl h-20"
                  placeholder="Description for search results..."
                />
              </div>
            </div>
          )}
        </div>

        {/* Right Panel - Mini Preview */}
        <div className="hidden lg:block">
          <div className="sticky top-24">
            <h3 className="text-sm font-medium mb-3 text-muted-foreground">Preview</h3>
            <div 
              className="rounded-xl overflow-hidden border border-white/10 aspect-[9/16] max-h-[600px]"
              style={{ 
                backgroundColor: formData.background_color,
                fontFamily: formData.font_family
              }}
            >
              {/* Mini preview */}
              <div className="p-4 h-full overflow-y-auto">
                {formData.cover_image && (
                  <div 
                    className="h-24 rounded-lg bg-cover bg-center mb-4"
                    style={{ backgroundImage: `url(${getImageUrl(formData.cover_image)})` }}
                  />
                )}
                {formData.profile_image && (
                  <img src={getImageUrl(formData.profile_image)} className="w-16 h-16 rounded-full mx-auto -mt-12 border-4 object-cover" style={{ borderColor: formData.background_color }} />
                )}
                <div className="text-center mt-2">
                  <h4 className="font-bold" style={{ color: formData.text_color }}>{formData.headline || formData.title || 'Page Title'}</h4>
                  <p className="text-sm opacity-70" style={{ color: formData.text_color }}>{formData.tagline}</p>
                </div>
                
                {formData.whatsapp_number && (
                  <div className="mt-4 p-3 rounded-lg text-center" style={{ backgroundColor: '#25D36620' }}>
                    <FaWhatsapp className="w-6 h-6 mx-auto text-green-500" />
                    <p className="text-xs mt-1" style={{ color: formData.text_color }}>WhatsApp</p>
                  </div>
                )}

                {formData.social_links.length > 0 && (
                  <div className="flex justify-center gap-2 mt-4 flex-wrap">
                    {formData.social_links.map((link, i) => {
                      const platform = SOCIAL_PLATFORMS.find(p => p.id === link.platform);
                      const Icon = platform?.icon || Globe;
                      return (
                        <div key={i} className="w-8 h-8 rounded-full flex items-center justify-center" style={{ backgroundColor: platform?.color + '20' }}>
                          <Icon className="w-4 h-4" style={{ color: platform?.color }} />
                        </div>
                      );
                    })}
                  </div>
                )}

                {formData.gallery_images.length > 0 && (
                  <div className="mt-4 grid grid-cols-2 gap-1">
                    {formData.gallery_images.slice(0, 4).map((img, i) => (
                      <img key={i} src={getImageUrl(img.url)} className="w-full h-12 object-cover rounded" />
                    ))}
                  </div>
                )}
              </div>
            </div>
            <p className="text-xs text-center text-muted-foreground mt-2">
              /p/{formData.slug || 'page-slug'}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PageEditor;
